import Fuse from "fuse.js";

/**
 * Match tiers, ordered from best to worst. A lower tier value wins.
 * Comparisons are case-insensitive.
 */
enum MatchTier {
  Exact = 0, // normalized name === normalized query
  Prefix = 1, // normalized name starts with normalized query
  Substring = 2, // normalized name contains normalized query
  Subsequence = 3, // query chars appear in order within the name
  Fuzzy = 4, // only matched fuzzily by fuse.js (typo tolerance)
  None = 5, // no match
}

/**
 * Lowercase and strip everything but letters/digits, so that spacing and
 * camelCase boundaries are ignored: both "shop status" and "shopstatus"
 * normalize to "shopstatus", and "shopOrderStatus" to "shoporderstatus".
 */
function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** True if every char of `query` appears in `name`, in order (not necessarily adjacent). */
function isSubsequence(query: string, name: string): boolean {
  if (!query) return true;
  let i = 0;
  for (let j = 0; j < name.length && i < query.length; j++) {
    if (name[j] === query[i]) i++;
  }
  return i === query.length;
}

function matchTier(name: string, query: string): MatchTier {
  const n = normalize(name);
  const q = normalize(query);

  if (!q) return MatchTier.None;
  if (n === q) return MatchTier.Exact;
  if (n.startsWith(q)) return MatchTier.Prefix;
  if (n.includes(q)) return MatchTier.Substring;
  if (isSubsequence(q, n)) return MatchTier.Subsequence;
  return MatchTier.None;
}

/**
 * Sort an array of items by relevance to a search term.
 *
 * Matching ignores spacing and camelCase boundaries, so "shop status" and
 * "shopstatus" both match "shopOrderStatus" (as a subsequence). fuse.js adds
 * typo tolerance for anything that doesn't match structurally.
 *
 * Results are ranked in tiers (exact > prefix > substring > subsequence >
 * fuzzy) so the most complete match wins outright. Within a tier, shorter
 * names rank first — e.g. "Landing Page" above "Landing Page Status" — and the
 * fuse.js score is the final tiebreaker.
 */
export function fuzzySort<T>(items: T[], searchTerm: string, getText: (item: T) => string): T[] {
  if (!searchTerm.trim()) {
    return items;
  }

  const itemsWithName = items.map((item) => ({ ...item, name: getText(item) }));

  const fuse = new Fuse(itemsWithName, {
    keys: ["name"],
    threshold: 0.3, // Lower threshold = more strict matching (0 = exact match, 1 = match anything)
    includeScore: true,
    minMatchCharLength: 1,
  });

  // fuse scores, keyed by the original item reference, used for the fuzzy tier
  // and as a tiebreaker (lower = better).
  const fuzzyScores = new Map<T, number>();
  for (const result of fuse.search(searchTerm)) {
    // refIndex maps back to the original (un-spread) item.
    fuzzyScores.set(items[result.refIndex], result.score ?? 1);
  }

  return items
    .map((item) => {
      const text = getText(item);
      let tier = matchTier(text, searchTerm);
      const fuzzyScore = fuzzyScores.get(item);
      if (tier === MatchTier.None && fuzzyScore !== undefined) {
        tier = MatchTier.Fuzzy;
      }
      return { item, tier, length: text.length, score: fuzzyScore ?? 1 };
    })
    .filter((entry) => entry.tier !== MatchTier.None)
    .sort((a, b) => {
      // 1. Better match tier first
      if (a.tier !== b.tier) return a.tier - b.tier;
      // 2. Within a tier, shorter names first
      if (a.length !== b.length) return a.length - b.length;
      // 3. Fall back to fuse.js score (lower = better)
      return a.score - b.score;
    })
    .map((entry) => entry.item);
}
